// ============================================================================
// APPROVAL HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { approvalService } from 'api/services/approvalService';
import { auditTrailService } from 'api/services/auditTrailService';
import type { ApprovalDashboardItem, ApprovalDetailItem, ApprovalActionPayload, ApprovalAuditReportEntry } from 'api/types/approval.types';
import type { AuditTrailEntry } from 'api/types/auditTrail.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'approvals';

export const useApprovalDashboard = () => {
  const [data, setData] = useState<ApprovalDashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await approvalService.getDashboard({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useDataChangeListener(DATA_KEY, fetchDashboard);

  return { data, loading, error, refetch: fetchDashboard };
};

export const useApprovalDetail = () => {
  const [data, setData] = useState<ApprovalDetailItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async (recordType: string, recordId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await approvalService.getDetail(recordType, recordId);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, loading, error, fetchDetail, reset };
};

export const useApprovalAction = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const processAction = useCallback(async (payload: ApprovalActionPayload) => {
    setLoading(true);
    setError(null);
    try {
      await approvalService.processAction(payload);
      // Approval actions affect multiple data types — notify all related
      notifyDataChanged(DATA_KEY);
      notifyDataChanged('addon-requests');
      notifyDataChanged('closings');
      notifyDataChanged('combined');
      notifyDataChanged('loan-mappings');
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { processAction, loading, error };
};

export const useApprovalAuditReport = () => {
  const [data, setData] = useState<ApprovalAuditReportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await approvalService.getAuditReport();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export const useDataChangeAuditTrail = () => {
  const [data, setData] = useState<AuditTrailEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditTrailService.getAll();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};
